import React, {Ref, useCallback, useEffect, useImperativeHandle, useRef, useState, useTransition} from "react";
import {MapContainer, TileLayer, Marker, Popup, LayerGroup, useMapEvents} from 'react-leaflet';
import {createControlComponent} from '@react-leaflet/core';
import {HitlistItem, MapCluster, MapSearchResult, RatingProfileId, SearchState} from "../models/models";
import L, {latLng, latLngBounds} from 'leaflet';
import {Actionable, Button, useTheme} from "reshaped";
import {GeoPosition, MapInfo, SearchQuery} from "../models/search_query";
import {EntryTile} from "./entry_tile";
import {mapSearch} from "../utils/api_client";
import {useTranslation} from "react-i18next";
import {useGeoPosition} from "../utils/geo_position_provider";
import {urlWithRatingProfileIdParam} from "../utils/helpers";

export type LoadingStatus = "loading" | "loaded" | "stale";
type ColorMode = "light" | "dark";

function clusterIcon(label: string, colorMode: ColorMode = "light"): L.DivIcon {
    const iconSrc = clusterIconUrl(colorMode);
    return L.divIcon({
        className: '!flex flex-col items-center gap-1',
        html: `
            <img src="${iconSrc}" alt="" class="h-43px" />
            <div class="text-center font-medium text-caption-1 text-neutral">${label}</div>`,
        iconSize: [90, 70],
        iconAnchor: [45, 42]
    });
}

const locationIcon = new L.Icon({
    iconUrl: RAILS_ASSET_URL("/leaflet/location.svg"),
    iconSize: [36, 36],
    iconAnchor: [18, 18]
});

function unknownIcon(colorMode: ColorMode = "light"): L.DivIcon {
    const iconSrc = clusterIconUrl(colorMode);
    return new L.Icon({
        iconUrl: iconSrc,
        iconSize: [34, 43],
        iconAnchor: [17, 42]
    });
}

const entryCompletelyIcon = new L.Icon({
    iconUrl: RAILS_ASSET_URL("/leaflet/map_pin_completely.svg"),
    iconSize: [34, 43],
    iconAnchor: [17, 42]
});

const entryPartiallyIcon = new L.Icon({
    iconUrl: RAILS_ASSET_URL("/leaflet/map_pin_partially.svg"),
    iconSize: [34, 43],
    iconAnchor: [17, 42]
});

const entryBadlyIcon = new L.Icon({
    iconUrl: RAILS_ASSET_URL("/leaflet/map_pin_badly.svg"),
    iconSize: [34, 43],
    iconAnchor: [17, 42]
});

function clusterIconUrl(colorMode: ColorMode = "light"): string {
    const iconSrc = colorMode === "light"
        ? RAILS_ASSET_URL("/leaflet/map_pin_cluster_light.svg")
        : RAILS_ASSET_URL("/leaflet/map_pin_cluster_dark.svg");
    return iconSrc;
}

function entryIcon(entry: HitlistItem, colorMode: ColorMode = "light") {
    switch (entry.entry.accessibility?.grade) {
        case "completely":
            return entryCompletelyIcon;
        case "partially":
            return entryPartiallyIcon;
        case "badly":
            return entryBadlyIcon;
        default:
            return unknownIcon(colorMode);
    }
}

function mapInfoAreEqual(a: MapInfo | null, b: MapInfo | null): boolean {
    if (a === null || b === null) {
        return a === b;
    }

    const isZoomEqual = a.zoom === b.zoom;

    const areBoundsIncluded = b.bounds?.contains(a.bounds!);
    return isZoomEqual && (areBoundsIncluded !== null ? areBoundsIncluded! : false);
}

function areOptionalFieldsEqual<T>(
    a: T | undefined,
    b: T | undefined,
    equalityFn: (a: T, b: T) => boolean
): boolean {
    if (a === undefined && b === undefined) {
        return true;
    }
    if (a === undefined || b === undefined) {
        return false;
    }
    return equalityFn(a, b);
}

function mapInfoFromSearchQuery(query: SearchQuery | null): MapInfo | null {
    if (query === null || (!query.center && !(query.northEast || query.southWest))) {
        return null;
    }

    return {
        center: query.center ? latLng(query.center) : undefined,
        bounds: query.southWest && query.northEast ? latLngBounds(query.southWest, query.northEast) : undefined,
        zoom: query.zoom,
    };
}

function mapInfoFromMap(map: L.Map): MapInfo {
    return {
        center: map.getCenter(),
        bounds: map.getBounds(),
        zoom: map.getZoom()
    };
}

export type MapAPI = {
    setView: (center: GeoPosition | null) => void;
    invalidateSize: () => void;
    reload: () => void;
}

interface MapProps {
    searchState: SearchState;
    placeTagPosition: GeoPosition | null;
    onMapSearch: (mapInfo: MapInfo) => void;
    onLoadingStatusChange?: (status: LoadingStatus) => void;
    sidebarPaddingLeft?: number;
    ref?: Ref<MapAPI>;
}

export default function Map({ searchState, placeTagPosition, onMapSearch, onLoadingStatusChange, sidebarPaddingLeft = 0, ref }: MapProps) {
    const { t } = useTranslation();
    const currentPosition = useGeoPosition();
    const [isPending, startTransition] = useTransition();
    const [mapSearchResult, setMapSearchResult] = useState<MapSearchResult | null>(null);
    const [nextMapInfo, setNextMapInfo] = useState<MapInfo | null>(null);
    const mapRef = useRef<L.Map>(null);
    const { colorMode } = useTheme();

    const handleMapMoved = useCallback((newMapInfo: MapInfo, autoReload: boolean) => {
        setNextMapInfo(newMapInfo);

        // automatically do map search on initial map load
        if (autoReload || !nextMapInfo) {
            onMapSearch(newMapInfo);
        }
    }, [setNextMapInfo, onMapSearch, nextMapInfo]);

    useImperativeHandle(ref, () => {
        return {
            setView(center: GeoPosition | null) {
                if (center && mapRef.current) {
                    // use once event to detect when the map has scrolled to the setView position
                    mapRef.current.once("moveend zoomend", () => {
                        if (mapRef.current) {
                            const newMapInfo = mapInfoFromMap(mapRef.current);
                            handleMapMoved(newMapInfo, true);
                        }
                    });
                    mapRef.current.setView(center, 15);
                }
            },
            invalidateSize() {
                mapRef.current?.invalidateSize();
            },
            reload() {
                handleReload();
            }
        };
    }, [handleMapMoved]);

    useEffect(() => {
        if (searchState.mapInfo) {
            startTransition(async () => {
                try {
                    const result = searchState.mapInfo?.bounds ? await mapSearch(searchState) : null;
                    startTransition(() => {
                        setMapSearchResult(result);
                    })
                } catch {
                    startTransition(() => {
                        setMapSearchResult(null);
                    })
                }
            });
        }
    }, [searchState, handleMapMoved]);

    const { mapInfo, ratingProfileId } = searchState;
    const {query, entries, clusters } = mapSearchResult ?? {query: null, entries: [], clusters: []};
    const loadingStatus = isPending ? "loading" : mapInfoAreEqual(nextMapInfo, mapInfoFromSearchQuery(query)) ? "loaded" : "stale";

    const handleReload = () => {
        if (nextMapInfo) {
            onMapSearch(nextMapInfo);
        }
    }

    useEffect(() => {
        onLoadingStatusChange?.(loadingStatus);
    }, [loadingStatus, onLoadingStatusChange]);

    const handleLocateClick = () => {
        if (mapRef.current) {
            let targetPosition: GeoPosition | null = null;
            if (placeTagPosition) {
                targetPosition = placeTagPosition;
            } else if (currentPosition.position) {
                targetPosition = currentPosition.position;
            }

            if (targetPosition) {
                // use once event to detect when the map has scrolled to the setView position
                mapRef.current.once("moveend zoomend", () => {
                    if (mapRef.current) {
                        const newMapInfo = mapInfoFromMap(mapRef.current);
                        handleMapMoved(newMapInfo, true);
                    }
                });
                mapRef.current.setView(targetPosition, 15);
            }
        }
    }

    class ZoomWithLocateControl extends L.Control.Zoom {
        onAdd(map: L.Map): HTMLElement {
            // Call the original onAdd to get the container
            const container = L.Control.Zoom.prototype.onAdd?.call(this, map);

            // Create custom locate button
            if (placeTagPosition || currentPosition.position) {
                const customButton = L.DomUtil.create('a', 'leaflet-control-locate', container);
                container?.insertBefore(customButton, container.firstChild);
                customButton.innerHTML = `
                    <span class="sr-only">${t("map.locate_button")}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                             <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
                    </svg>`;
                customButton.role = 'button';
                customButton.href = '#';
                customButton.title = t("map.locate_button");

                L.DomEvent.on(customButton, 'click', function (e) {
                    L.DomEvent.stop(e);
                    handleLocateClick();
                });
            }

            return container!;
        }
    }

    const ZoomControl = createControlComponent((props) => new ZoomWithLocateControl(props));

    const colorModeBasedMapId = colorMode === "light" ? "ginto/ck9e69gth273b1itgoq9lx6yb" : "ginto/cma10mfgq01pi01r37kxu8n8y"
    const boundsOptions: L.FitBoundsOptions | undefined =
        sidebarPaddingLeft > 0 ? { paddingTopLeft: [sidebarPaddingLeft, 0] } : undefined;
    return (
        <div className="h-full" aria-hidden={true}>
            <MapContainer ref={mapRef} bounds={mapInfo?.bounds} boundsOptions={boundsOptions} center={mapInfo?.center} zoom={mapInfo?.zoom} zoomControl={false} scrollWheelZoom={true} className="h-full">
                <TileLayer
                    attribution='© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>'
                    url={`https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=${(window as any).env.mapboxToken}`}
                    tileSize={512}
                    maxZoom={18}
                    zoomOffset={-1}
                    id={colorModeBasedMapId}
                />
                <ZoomControl position={"bottomright"} />
                <CurrentLocationMarker placeTagPosition={placeTagPosition} />
                <Markers entries={entries} clusters={clusters} ratingProfileId={ratingProfileId} onMapMoved={handleMapMoved} />
            </MapContainer>
        </div>
    );
};


interface CurrentLocationMarkerProps {
    placeTagPosition: GeoPosition | null;
}

function CurrentLocationMarker({ placeTagPosition }: CurrentLocationMarkerProps) {
    return (placeTagPosition &&
        <Marker key={`current_location`} position={[placeTagPosition.lat, placeTagPosition.lng]} zIndexOffset={100} icon={locationIcon}></Marker>
    );
}


interface MarkersProps {
    entries: Array<HitlistItem>;
    clusters: Array<MapCluster>;
    ratingProfileId: RatingProfileId;
    onMapMoved: (mapInfo: MapInfo, autoReload: boolean) => void;
}

function Markers({ entries, clusters, ratingProfileId, onMapMoved }: MarkersProps) {
    const { t } = useTranslation();
    const { colorMode } = useTheme();
    const map = useMapEvents({
        moveend: () => {
            handleMapMoved(map);
        },
        zoomend: () => {
            handleMapMoved(map, true);
        },
    });

    useEffect(() => {
        handleMapMoved(map);
    }, [map])

    function handleMapMoved(map: L.Map, autoReload: boolean = false) {
        if (map) {
            onMapMoved(mapInfoFromMap(map), autoReload);
        }
    }

    const entryMarkers = entries.map(result => {
        const url = urlWithRatingProfileIdParam(result.entry.webUrl, ratingProfileId);
        return (
            <Marker key={`entry_marker_${result.entry.id}`}
                    position={[result.entry.position.lat, result.entry.position.lng]} icon={entryIcon(result, colorMode)}>
                <Popup maxWidth={420} minWidth={320} closeButton={false} offset={[0, -30]}>
                    <Actionable href={url.href} attributes={{target: "_blank"}} fullWidth={true}>
                        <EntryTile entry={result.entry} distance={result.distance}/>
                    </Actionable>
                </Popup>
            </Marker>);
        }
    );
    const clusterMarkers = clusters.map(cluster => {
        const center = latLng(cluster.lat, cluster.lng);
        const eventHandlers = {
                click: () => {
                    map.setView(center, cluster.nextZoomlevel);
                },
            }

        return (
            <Marker key={`cluster_marker_${cluster.lat}_${cluster.lng}`} position={[cluster.lat, cluster.lng]} icon={clusterIcon(t("map.cluster_label", { count: cluster.numEntries }), colorMode)} eventHandlers={eventHandlers}></Marker>);
    }
    );
    return (
        <LayerGroup>
            { entryMarkers }
            { clusterMarkers }
        </LayerGroup>
    );
}


interface ReloadButtonProps {
    status: LoadingStatus;
    onReload: () => void;
}

export function ReloadButton({status, onReload}: ReloadButtonProps) {
    const { t } = useTranslation();

    const loading = status == "loading";
    return (
        status != "loaded" ?
            <Button loading={loading} onClick={onReload} elevated={true} color="neutral" className={ loading ? "" : "bg-page!"}>
                {t("map.reload_button")}
            </Button> : null
    )
}
